import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Button from '../components/ui/Button';

const { FiStar, FiMessageSquare, FiTrendingUp, FiShield, FiArrowRight, FiCheckCircle } = FiIcons;

const Home = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden bg-primary text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
        <div className="container relative px-4 mx-auto max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-1.5 mb-6 text-sm font-semibold tracking-wider uppercase rounded-full bg-secondary/20 text-secondary">
                Powered by Review Guru
              </span>
              <h1 className="mb-6 text-5xl font-extrabold tracking-tight md:text-6xl text-white">
                Get More 5-Star Reviews <span className="text-secondary">Automatically.</span>
              </h1>
              <p className="mb-10 text-xl leading-relaxed text-indigo-100">
                Review Builder Pro helps your business collect more Google reviews and improve your online reputation effortlessly.
              </p>
              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Button to="/free-trial" variant="primary" size="lg">Start Free Trial</Button>
                <Button to="/demo" variant="outline" size="lg" className="border-indigo-400 text-white hover:bg-white/10">
                  <SafeIcon icon={FiStar} className="w-5 h-5 mr-2" /> Watch Demo
                </Button>
              </div>
              <p className="mt-6 text-sm text-indigo-300">14-Day Free Trial • No Credit Card Required</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats/Logos Strip */}
      <section className="py-10 bg-white border-b">
        <div className="container px-4 mx-auto max-w-7xl">
          <p className="mb-6 text-sm font-medium text-center text-slate-500 uppercase tracking-widest">Trusted by growing businesses</p>
          <div className="flex flex-wrap items-center justify-center gap-12 opacity-50 grayscale">
            <div className="text-2xl font-bold">LocalBites</div>
            <div className="text-2xl font-bold">Apex Medical</div>
            <div className="text-2xl font-bold">City Retail</div>
            <div className="text-2xl font-bold">UnlimitedPro</div>
          </div>
        </div>
      </section>

      {/* How It Works Summary */}
      <section className="py-24 bg-slate-50">
        <div className="container px-4 mx-auto max-w-7xl">
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl text-primary">How Review Builder Pro Works</h2>
            <p className="text-lg text-slate-600">A simple, automated system to transform your customer experiences into glowing online reviews.</p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              { 
                icon: FiMessageSquare, 
                title: "1. Send Requests", 
                desc: "Automatically send SMS or email review requests after a purchase or visit." 
              },
              { 
                icon: FiStar, 
                title: "2. Capture Happy Customers", 
                desc: "Satisfied customers are directed straight to Google to leave a 5-star review." 
              },
              { 
                icon: FiShield, 
                title: "3. Manage Feedback", 
                desc: "Unhappy customers are redirected to a private feedback form, protecting your public reputation." 
              }
            ].map((step, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="p-8 bg-white shadow-soft rounded-2xl text-center relative"
              >
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 rounded-full bg-secondary/10 text-secondary">
                  <SafeIcon icon={step.icon} className="w-8 h-8" />
                </div>
                <h3 className="mb-3 text-xl font-bold text-primary">{step.title}</h3>
                <p className="text-slate-600">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-24 bg-white">
        <div className="container px-4 mx-auto max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6 text-secondary">
              {[...Array(5)].map((_, i) => <SafeIcon key={i} icon={FiStar} className="w-6 h-6 fill-current" />)}
            </div>
            <blockquote className="mb-8 text-2xl font-medium leading-relaxed md:text-4xl text-primary">
              "This system helped us get 80+ new Google reviews in 3 months. Our local search ranking skyrocketed and we're seeing more foot traffic than ever."
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <img 
                src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&q=80" 
                alt="Michael R." 
                className="w-12 h-12 rounded-full object-cover" 
              />
              <div className="text-left">
                <div className="font-bold text-primary">Michael R.</div>
                <div className="text-sm text-slate-500">Owner, Joe's Auto Repair</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary">
        <div className="container px-4 mx-auto max-w-4xl text-center">
          <h2 className="mb-6 text-3xl font-bold text-primary md:text-4xl">Ready to dominate your local market?</h2>
          <p className="mb-10 text-xl text-primary/80">Join thousands of businesses using Review Builder Pro to grow their reputation.</p>
          <Button to="/free-trial" variant="secondary" size="lg" className="bg-primary text-white hover:bg-primary-light">
            Start Your 14-Day Free Trial
          </Button>
          <p className="mt-6 text-sm text-primary/60">No credit card required. Cancel anytime.</p>
        </div>
      </section>
    </div>
  );
};

export default Home;